import React from 'react';
import JSON5 from 'json5';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import pt from 'prop-types';

import { postEasyblocksBackendDocumentUpdated } from 'components/storefront/editor/utils/post-message';
import { convertPage, convertLayoutSectionGroup } from './utils/conversion';
import { getFormValues } from './utils/easyblocks';

/** @typedef {import('@swell/apps-sdk').SwellTheme} SwellTheme */

/**
 * @typedef {object} Props
 * @property {SwellTheme} swellTheme
 */

const SAVE_DEBOUNCE_WAIT = 1000;

// Create context
export const EasyblocksBackendContext = React.createContext({
  storefront: {},
  getEasyblocksBackend() {},
  easyblocksState: {},
  saving: false,
  lookup: {},
  onClickGetHelp() {},
  enableSave() {},
  disableSave() {},
});

/**
 * @extends React.Component<Props>
 */
class EasyblocksBackendProvider extends React.Component {
  static propTypes = {
    children: pt.any,
    storefront: pt.object,
    storefrontConfigs: pt.object,
    lookup: pt.object,
    page: pt.object,
    editorConfig: pt.object,
    swellTheme: pt.object,
    sectionGroups: pt.array,
    updatePage: pt.func,
    replacePage: pt.func,
    updateThemeConfig: pt.func,
    onAfterSave: pt.func,
    fetchThemeConfig: pt.func,
    onUpdateStorefrontSettings: pt.func,
  };

  static contextTypes = {
    notifyError: pt.func.isRequired,
    openModal: pt.func.isRequired,
    closeModal: pt.func.isRequired,
    onClickGetHelp: pt.func.isRequired,
    queryLookupResults: pt.func.isRequired,
  };

  constructor(props, context) {
    super(props);

    this.state = {
      getEasyblocksBackend: this.getEasyblocksBackend,
      easyblocksState: {
        documents: {},
        templates: {},
      },
      saving: false,
      canSave: true,
      loaded: false,
      storefront: props.storefront,
      storefrontConfigs: props.storefrontConfigs,
      editorConfig: props.editorConfig,
      lookup: props.lookup,
      openModal: context.openModal,
      closeModal: context.closeModal,
      onClickGetHelp: context.onClickGetHelp,
      queryLookupResults: context.queryLookupResults,
      onUpdateStorefrontSettings: props.onUpdateStorefrontSettings,
      enableSave: this.enableSave.bind(this),
      disableSave: this.disableSave.bind(this),
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { lookup, storefrontConfigs } = this.props;
    const { canSave } = this.state;

    if (lookup !== prevProps.lookup) {
      this.setState({ lookup });
    }

    if (storefrontConfigs !== prevProps.storefrontConfigs) {
      this.setState({ storefrontConfigs });
    }

    if (canSave && !prevState.canSave) {
      this.onSave();
    }
  }

  enableSave() {
    this.setState({ canSave: true });
  }

  disableSave() {
    this.setState({ canSave: false });
  }

  onSave = debounce(async () => {
    const { notifyError } = this.context;
    const { canSave } = this.state;

    if (!canSave) {
      return;
    }

    try {
      await this.save();
    } catch (error) {
      notifyError(error.message);
    } finally {
      this.setState({ saving: false });
    }
  }, SAVE_DEBOUNCE_WAIT);

  async save() {
    const {
      page: { id: pageId },
      storefront,
      editorConfig,
      swellTheme,
      sectionGroups,
      fetchThemeConfig,
      updatePage,
      replacePage,
      updateThemeConfig,
      onAfterSave,
    } = this.props;
    const { page } = editorConfig.globals;

    const pageConfigPath = swellTheme.getPageConfigPath(pageId);

    const entry = getFormValues();
    const groups = [
      ...sectionGroups,
      { source: swellTheme.themeSettingFilePath },
    ];

    const configs = await Promise.all(
      groups.map((group) =>
        fetchThemeConfig(storefront.theme_id, group.source),
      ),
    );

    if (!configs.some((config) => config.file_path === pageConfigPath)) {
      throw new Error(`Could not find page config file (${pageConfigPath})`);
    }

    if (
      !configs.some(
        (config) => config.file_path === swellTheme.themeSettingFilePath,
      )
    ) {
      throw new Error(
        `Could not find settings config file (${swellTheme.themeSettingFilePath})`,
      );
    }

    let pageConfigIndex = -1;
    let shouldReplacePage = false;
    let updatedPageConfigId = '';
    const updatedConfigIds = [];

    const promises = configs.reduce((acc, config, index) => {
      const sectionGroup = groups[index];
      const parsedConfig = JSON5.parse(config.file_data);

      switch (sectionGroup.source) {
        // Update settings config
        case swellTheme.themeSettingFilePath: {
          const updated = swellTheme.updateSettings(entry, parsedConfig);

          if (!isEqual(parsedConfig, updated)) {
            updatedConfigIds.push(config.id);
            acc.push(updateThemeConfig(config.id, updated));
          }

          break;
        }

        // Update page config
        case pageConfigPath: {
          const updated = convertPage(pageId, entry, sectionGroup);

          updatedPageConfigId = updated.id;

          const updatedSlug = updated.page?.slug;
          const isSlugUpdated =
            updatedSlug && updatedSlug !== parsedConfig.page?.slug;

          shouldReplacePage = page.custom && isSlugUpdated;
          const shouldUpdatePageConfig =
            shouldReplacePage || !isEqual(parsedConfig, updated);

          if (shouldUpdatePageConfig) {
            updatedConfigIds.push(config.id);

            acc.push(
              shouldReplacePage
                ? replacePage(pageId, updated)
                : updatePage(updated),
            );

            pageConfigIndex = acc.length - 1;
          }

          break;
        }

        // Update layout section group (header-group, footer-group, etc.)
        default: {
          const updated = convertLayoutSectionGroup(
            entry,
            sectionGroup,
            parsedConfig,
          );

          if (!isEqual(parsedConfig, updated)) {
            updatedConfigIds.push(config.id);
            acc.push(updateThemeConfig(config.id, updated));
          }

          break;
        }
      }

      return acc;
    }, []);

    if (promises.length <= 0) {
      return;
    }

    this.setState({ saving: true });

    // Update configs
    const updateResults = await Promise.allSettled(promises).then(
      this.processUpdateResults,
    );

    // Return if there are no updated configs
    if (!updateResults.some(Boolean)) {
      return;
    }

    // Fetch updated configs
    const isPageUpdated =
      pageConfigIndex >= 0 ? updateResults[pageConfigIndex] : false;

    const redirectPageId =
      isPageUpdated && shouldReplacePage ? updatedPageConfigId : null;

    await onAfterSave({
      updatedConfigIds,
      isPageUpdated,
      redirectPageId,
    });
  }

  processUpdateResults(updateResults) {
    return updateResults.map((updateResult) => {
      const isFulfilled = updateResult.isFulfilled();
      const value = updateResult.value();

      if (!isFulfilled || !value || value.errors || value.error) {
        return false;
      }

      return true;
    });
  }

  getEasyblocksBackend = (pageId, { template }) => {
    if (!template) {
      return null;
    }

    this.setDocument(
      {
        id: pageId,
        entry: template.entry,
        isUserDefined: false,
        version: template.version || 1,
      },
      () => postEasyblocksBackendDocumentUpdated(),
    );

    return {
      documents: {
        // Fetching from local cache
        get: async ({ id }) => {
          console.log('Easyblocks backend documents.get()', id);

          return this.state.easyblocksState.documents[id];
        },
        // Saving in local cache
        create: async (payload) => {
          console.log('Easyblocks backend documents.create()', payload);

          const document = {
            id: pageId,
            entry: payload.entry,
            version: 1,
          };

          this.setDocument(document);

          return document;
        },
        /**
         * Update local cache and update the server
         *
         * Note: There are 2 sources that trigger the update:
         *  1.Every 5 seconds, Easyblocks checks if the document has changed,
         *    and if it has, it triggers an update.
         *    Also, Easyblocks increments the document version and includes it in the payload.
         *  2.Our custom subscription to form changes (storefront/editor/utils/easyblocks.js #onFormUpdated),
         *    which is triggered immediately on every document change.
         *    This is necessary to avoid delays in displaying changes in the UI.
         *    This update call does not increment the document version and does not include the version in the payload.
         *
         * @param {object} payload
         * @returns {Promise<object>}
         */
        update: async (payload) => {
          console.log('Easyblocks backend documents.update()', payload);

          const { easyblocksState, loaded } = this.state;
          const isEasyblocksUpdate = Boolean(payload.version);
          const currentDocument = easyblocksState.documents[pageId];
          const document = {
            id: pageId,
            entry: payload.entry,
            version: payload.version
              ? payload.version + 1
              : currentDocument.version,
          };

          this.setDocument(document, () => {
            if (loaded && !isEasyblocksUpdate) {
              postEasyblocksBackendDocumentUpdated();
              this.onSave();
            } else if (!loaded && isEasyblocksUpdate) {
              return this.setState({ loaded: true });
            }
          });

          return document;
        },
      },
      templates: {
        get: async (payload) => {
          console.log('Easyblocks backend templates.get()', payload);
          return {};
        },
        getAll: async () => {
          console.log('Easyblocks backend get templates.getAll()');
          return [];
        },
        create: async (payload) => {
          console.log('Easyblocks backend get templates.create()', payload);
          return {};
        },
        update: async (payload) => {
          console.log('Easyblocks backend get templates.update()', payload);
          return {};
        },
        delete: async (payload) => {
          console.log('Easyblocks backend get templates.delete()', payload);
          return;
        },
      },
    };
  };

  setDocument(document, callback) {
    if (!document) {
      return;
    }

    const { id, entry, version } = document;

    this.setState(
      (state) => ({
        ...state,
        easyblocksState: {
          ...state.easyblocksState,
          documents: {
            ...state.easyblocksState.documents,
            [id]: { id, entry, version },
          },
        },
      }),
      callback,
    );
  }

  render() {
    return (
      <EasyblocksBackendContext.Provider value={this.state}>
        {this.props.children}
      </EasyblocksBackendContext.Provider>
    );
  }
}

export const withEasyblocksBackend = (Component) => {
  class WithEasyblocksBacked extends React.PureComponent {
    render() {
      return (
        <EasyblocksBackendProvider>
          <Component {...this.props} />
        </EasyblocksBackendProvider>
      );
    }
  }

  return WithEasyblocksBacked;
};

export default EasyblocksBackendProvider;
